
import { useState } from "react";
import { Avatar } from "../ui/avatar";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface GroupPostComment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles: {
    username: string;
    avatar_url: string;
  };
}

interface GroupPostCommentsProps {
  postId: string;
  comments: GroupPostComment[];
  onCommentAdded: () => void;
}

export const GroupPostComments = ({ postId, comments, onCommentAdded }: GroupPostCommentsProps) => {
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Create a Map of comments by ID to remove duplicates
  const uniqueCommentsMap = new Map();
  comments.forEach(comment => {
    if (!uniqueCommentsMap.has(comment.id)) {
      uniqueCommentsMap.set(comment.id, comment);
    }
  });
  
  // Convert Map back to array
  const uniqueComments = Array.from(uniqueCommentsMap.values());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from('group_post_comments')
        .insert({
          post_id: postId,
          user_id: user.id,
          content: content.trim()
        });

      if (error) throw error;

      setContent("");
      onCommentAdded();
      toast({
        title: "Comment added",
        description: "Your comment has been posted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-2">
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write a comment..."
          className="min-h-[100px]"
        />
        <Button 
          type="submit" 
          disabled={isSubmitting || !content.trim()}
          className="w-full sm:w-auto"
        >
          Post Comment
        </Button>
      </form>

      <div className="space-y-4">
        {uniqueComments.map((comment) => (
          <div key={comment.id} className="flex gap-3">
            <Avatar className="w-8 h-8">
              <img 
                src={comment.profiles.avatar_url || "/placeholder.svg"} 
                alt={comment.profiles.username} 
              />
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">
                  {comment.profiles.username}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                </span>
              </div>
              <p className="text-sm mt-1">{comment.content}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
